import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export type DistortMode = "pinch" | "spherize" | "twirl";

export interface DistortParams {
  mode: DistortMode;
  amount: number;
}

function premultiply(src: Uint8ClampedArray, w: number, h: number): Float32Array {
  const out = new Float32Array(w * h * 4);
  const n = w * h;
  for (let i = 0; i < n; i++) {
    const o = i * 4;
    const a = src[o + 3]!;
    const af = a / 255;
    out[o] = src[o]! * af;
    out[o + 1] = src[o + 1]! * af;
    out[o + 2] = src[o + 2]! * af;
    out[o + 3] = a;
  }
  return out;
}

function unpremultiply(src: Float32Array, w: number, h: number): Uint8ClampedArray {
  const out = new Uint8ClampedArray(w * h * 4);
  const n = w * h;
  for (let i = 0; i < n; i++) {
    const o = i * 4;
    const a = src[o + 3]!;
    if (a <= 0) {
      out[o] = 0;
      out[o + 1] = 0;
      out[o + 2] = 0;
      out[o + 3] = 0;
      continue;
    }
    const inv = 255 / a;
    out[o] = Math.round(src[o]! * inv);
    out[o + 1] = Math.round(src[o + 1]! * inv);
    out[o + 2] = Math.round(src[o + 2]! * inv);
    out[o + 3] = Math.round(a);
  }
  return out;
}

function sampleBilinear(
  buf: Float32Array,
  w: number,
  h: number,
  fx: number,
  fy: number,
): [number, number, number, number] {
  const cx = Math.max(0, Math.min(w - 1, fx));
  const cy = Math.max(0, Math.min(h - 1, fy));

  const x0 = Math.floor(cx);
  const y0 = Math.floor(cy);
  const x1 = Math.min(x0 + 1, w - 1);
  const y1 = Math.min(y0 + 1, h - 1);
  const fracX = cx - x0;
  const fracY = cy - y0;
  const ix = 1 - fracX;
  const iy = 1 - fracY;

  const o00 = (y0 * w + x0) * 4;
  const o10 = (y0 * w + x1) * 4;
  const o01 = (y1 * w + x0) * 4;
  const o11 = (y1 * w + x1) * 4;

  const r =
    buf[o00]! * ix * iy +
    buf[o10]! * fracX * iy +
    buf[o01]! * ix * fracY +
    buf[o11]! * fracX * fracY;

  const g =
    buf[o00 + 1]! * ix * iy +
    buf[o10 + 1]! * fracX * iy +
    buf[o01 + 1]! * ix * fracY +
    buf[o11 + 1]! * fracX * fracY;

  const b =
    buf[o00 + 2]! * ix * iy +
    buf[o10 + 2]! * fracX * iy +
    buf[o01 + 2]! * ix * fracY +
    buf[o11 + 2]! * fracX * fracY;

  const a =
    buf[o00 + 3]! * ix * iy +
    buf[o10 + 3]! * fracX * iy +
    buf[o01 + 3]! * ix * fracY +
    buf[o11 + 3]! * fracX * fracY;

  return [r, g, b, a];
}

function distortPass(
  src: Float32Array,
  dst: Float32Array,
  w: number,
  h: number,
  mode: DistortMode,
  amount: number,
): void {
  const halfW = w / 2;
  const halfH = h / 2;

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const nx = (x / w) * 2 - 1;
      const ny = (y / h) * 2 - 1;
      const r = Math.sqrt(nx * nx + ny * ny);

      let sx: number;
      let sy: number;

      if (r < 1e-10) {
        sx = x;
        sy = y;
      } else {
        switch (mode) {
          case "pinch": {
            const p = amount / 100;
            const f = Math.pow(r, p);
            sx = (nx / r) * f * halfW + halfW;
            sy = (ny / r) * f * halfH + halfH;
            break;
          }
          case "spherize": {
            const f = r * (1 + (amount / 100) * (1 - r * r));
            sx = (nx / r) * f * halfW + halfW;
            sy = (ny / r) * f * halfH + halfH;
            break;
          }
          case "twirl": {
            const angle = (amount * Math.PI / 180) * (1 - r);
            const cosA = Math.cos(angle);
            const sinA = Math.sin(angle);
            const snx = nx * cosA - ny * sinA;
            const sny = nx * sinA + ny * cosA;
            sx = snx * halfW + halfW;
            sy = sny * halfH + halfH;
            break;
          }
        }
      }

      const [rC, gC, bC, aC] = sampleBilinear(src, w, h, sx, sy);
      const o = (y * w + x) * 4;
      dst[o] = rC;
      dst[o + 1] = gC;
      dst[o + 2] = bC;
      dst[o + 3] = aC;
    }
  }
}

export const DistortSuite: Filter<DistortParams> = {
  id: "distort-suite",
  displayName: "Distort",
  defaultParams: { mode: "twirl", amount: 50 },

  apply(
    input: PixelBuffer,
    params: DistortParams,
    _selection: Selection | null,
  ): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);

    if (w === 0 || h === 0) {
      out.data.set(input.data);
      return out;
    }

    if (Math.abs(params.amount) < 1e-6) {
      out.data.set(input.data);
      return out;
    }

    const pre = premultiply(input.data, w, h);
    const tmp = new Float32Array(w * h * 4);
    distortPass(pre, tmp, w, h, params.mode, params.amount);
    const result = unpremultiply(tmp, w, h);
    out.data.set(result);
    return out;
  },
};
